import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save } from 'lucide-react';
import HelpIcon from './help/HelpIcon';
import { getPurchaseOrders } from '../services/api';
import {
  MANUAL_TYPES,
  WASTE_REASONS,
  ABSOLUTE_TYPES,
  buildMovementPayload,
} from '../utils/movements';
import useEscapeKey from '../hooks/useEscapeKey';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import './CreateMovementModal.css';

const EMPTY_FORM = {
  itemId: '',
  type: 'STOCK_IN',
  quantity: '',
  reason: '',
  cost: '',
  costMode: 'total',
  expirationDate: '',
  reasonCode: 'EXPIRED',
  toRestaurantId: '',
  purchaseOrderId: '',
};

/** Clave i18n del botón de cada tipo manual. */
const TYPE_BUTTON_KEYS = {
  STOCK_IN: 'typeStockInLabel',
  STOCK_OUT: 'typeStockOutLabel',
  ADJUSTMENT: 'typeAdjustmentLabel',
  WASTE: 'typeWasteLabel',
  COUNT: 'typeCountLabel',
  TRANSFER: 'typeTransferLabel',
};

/** Órdenes de compra que todavía pueden recibir mercadería. */
const OPEN_PO_STATUSES = ['PENDING', 'SENT', 'RECEIVED'];

const formatAmount = (value) =>
  Number.isFinite(value) ? (Math.round(value * 100) / 100).toString() : '';

/**
 * Modal para registrar un movimiento manual (RES-24 / RES-41): ingreso con lote, costo (total o
 * por unidad) y orden de compra; merma con motivo en un clic (vencimiento preseleccionado);
 * ajuste; egreso; conteo; y transferencia a otro local (solo si la organización tiene más de uno).
 * @param {{isOpen: boolean, onClose: Function, onSave: Function, items?: Array, restaurants?: Array, currentRestaurantId?: string, initialItemId?: string, initialType?: string}} props - Props
 * @returns {JSX.Element|null} Modal
 */
function CreateMovementModal({
  isOpen,
  onClose,
  onSave,
  items = [],
  restaurants = [],
  currentRestaurantId = null,
  initialItemId = '',
  initialType = 'STOCK_IN',
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linkPurchaseOrder, setLinkPurchaseOrder] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState(null);
  const quantityRef = useRef(null);

  // Cada apertura arranca limpia; si la pantalla ya está filtrada por un insumo, viene elegido
  useEffect(() => {
    if (!isOpen) return;
    setFormData({ ...EMPTY_FORM, itemId: initialItemId || '', type: initialType || 'STOCK_IN' });
    setError(null);
    setLinkPurchaseOrder(false);
  }, [isOpen, initialItemId, initialType]);

  // Las órdenes de compra se piden recién cuando se quiere vincular una
  useEffect(() => {
    if (!isOpen || !linkPurchaseOrder || purchaseOrders !== null) return undefined;
    let cancelled = false;
    getPurchaseOrders()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.orders || [];
        setPurchaseOrders(list.filter((po) => OPEN_PO_STATUSES.includes(po.status)));
      })
      .catch(() => {
        if (!cancelled) setPurchaseOrders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, linkPurchaseOrder, purchaseOrders]);

  useEscapeKey(isOpen && !loading, onClose);

  if (!isOpen) return null;

  const otherRestaurants = restaurants.filter(
    (r) => r.restaurantId && r.restaurantId !== currentRestaurantId && r.status !== 'deleted'
  );
  const availableTypes = MANUAL_TYPES.filter(
    (type) => type !== 'TRANSFER' || otherRestaurants.length > 0
  );

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));
  const handleChange = (e) => setField(e.target.name, e.target.value);

  const selectType = (type) => {
    setFormData((prev) => ({ ...prev, type }));
    // Después del tipo lo próximo es la cantidad: menos clics para una merma (RES-41)
    if (quantityRef.current) quantityRef.current.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.itemId || formData.quantity === '') {
        throw new Error(t('inventory.movementsPage.requiredFieldsError'));
      }
      if (formData.type === 'TRANSFER' && !formData.toRestaurantId) {
        throw new Error(t('inventory.movementsPage.selectRestaurantError'));
      }

      await onSave(
        buildMovementPayload({
          ...formData,
          purchaseOrderId: linkPurchaseOrder ? formData.purchaseOrderId : '',
        })
      );
      onClose();
      setFormData(EMPTY_FORM);
    } catch (err) {
      // RES-42: el error del backend se muestra traducido por su código
      console.error('Error creating movement:', err.response?.data || err);
      setError(
        inventoryErrorMessage(
          t,
          err,
          err.response?.status === 400
            ? 'inventory.movementsPage.invalidRequest'
            : 'inventory.movementsPage.saveError'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedItem = items.find((i) => i.itemId === formData.itemId);
  const isAbsolute = ABSOLUTE_TYPES.includes(formData.type);
  const isStockIn = formData.type === 'STOCK_IN';
  const qty = parseFloat(formData.quantity);
  const cost = parseFloat(formData.cost);
  const costPreview =
    isStockIn && qty > 0 && cost >= 0
      ? formData.costMode === 'unit'
        ? t('inventory.movementsPage.costPreviewTotal', { amount: formatAmount(cost * qty) })
        : t('inventory.movementsPage.costPreviewUnit', {
            amount: formatAmount(cost / qty),
            unit: selectedItem?.unit || '',
          })
      : null;

  const poLabel = (po) => {
    const date = po.createdAt ? new Date(po.createdAt).toLocaleDateString() : '';
    return [`#${String(po.orderId).slice(-6)}`, date, po.supplierName].filter(Boolean).join(' · ');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>{t('inventory.movementsPage.registerTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="close-button"
            aria-label={t('common.close')}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="movement-form">
          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="movement-item">{t('inventory.movementsPage.productRequired')}</label>
            <select
              id="movement-item"
              name="itemId"
              value={formData.itemId}
              onChange={handleChange}
              required
              className="form-input"
            >
              <option value="">{t('inventory.movementsPage.selectProduct')}</option>
              {/* Insumos solo para costeo (trackStock=false) no aceptan movimientos (RES-22) */}
              {items
                .filter((item) => item.trackStock !== false)
                .map((item) => (
                  <option key={item.itemId} value={item.itemId}>
                    {item.name} ({t('inventory.movementsPage.currentStock')}: {item.stock}{' '}
                    {item.unit})
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label>
              {t('inventory.movementsPage.movementType')}{' '}
              <HelpIcon topicKey="inventory.movementTypes" />
            </label>
            <div
              className="type-selector"
              role="radiogroup"
              aria-label={t('inventory.movementsPage.movementType')}
            >
              {availableTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  role="radio"
                  aria-checked={formData.type === type}
                  className={`type-button type-${type.toLowerCase()} ${
                    formData.type === type ? 'selected' : ''
                  }`}
                  onClick={() => selectType(type)}
                >
                  {t(`inventory.movementsPage.${TYPE_BUTTON_KEYS[type]}`)}
                </button>
              ))}
            </div>
          </div>

          {formData.type === 'WASTE' && (
            <div className="form-group">
              <span className="form-label" id="movement-reason-code-label">
                {t('inventory.movementsPage.wasteReason')} *
              </span>
              <div
                className="reason-chips"
                role="radiogroup"
                aria-labelledby="movement-reason-code-label"
              >
                {WASTE_REASONS.map((code) => (
                  <button
                    key={code}
                    type="button"
                    role="radio"
                    aria-checked={formData.reasonCode === code}
                    data-reason={code}
                    className={`reason-chip${formData.reasonCode === code ? ' selected' : ''}`}
                    onClick={() => setField('reasonCode', code)}
                  >
                    {t(`inventory.movementsPage.wasteReasons.${code}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {formData.type === 'TRANSFER' && (
            <div className="form-group">
              <label htmlFor="movement-to-restaurant">
                {t('inventory.movementsPage.toRestaurant')} *
              </label>
              <select
                id="movement-to-restaurant"
                name="toRestaurantId"
                value={formData.toRestaurantId}
                onChange={handleChange}
                required
                className="form-input"
              >
                <option value="">{t('inventory.movementsPage.selectRestaurant')}</option>
                {otherRestaurants.map((r) => (
                  <option key={r.restaurantId} value={r.restaurantId}>
                    {r.name || r.restaurantId}
                  </option>
                ))}
              </select>
              <small className="help-text">{t('inventory.movementsPage.transferHelp')}</small>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="movement-quantity">
                {isAbsolute
                  ? formData.type === 'COUNT'
                    ? t('inventory.movementsPage.countedStock')
                    : t('inventory.movementsPage.realStock')
                  : t('inventory.movementsPage.quantity')}{' '}
                *
              </label>
              <input
                id="movement-quantity"
                ref={quantityRef}
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min={isAbsolute ? '0' : undefined}
                required
                className="form-input"
              />
              {selectedItem && <span className="unit-label">{selectedItem.unit}</span>}
            </div>

            {isStockIn && (
              <div className="form-group">
                <label htmlFor="movement-expiration">
                  {t('inventory.movementsPage.lotExpirationOptional')}
                </label>
                <input
                  id="movement-expiration"
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate || ''}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            )}
          </div>

          {isStockIn && (
            <div className="form-group">
              <div className="cost-header">
                <label htmlFor="movement-cost">{t('inventory.movementsPage.costOptional')}</label>
                <div
                  className="cost-mode-toggle"
                  role="radiogroup"
                  aria-label={t('inventory.movementsPage.costOptional')}
                >
                  {['total', 'unit'].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={formData.costMode === mode}
                      data-cost-mode={mode}
                      className={`cost-mode-button${formData.costMode === mode ? ' selected' : ''}`}
                      onClick={() => setField('costMode', mode)}
                    >
                      {t(
                        mode === 'unit'
                          ? 'inventory.movementsPage.costModeUnit'
                          : 'inventory.movementsPage.costModeTotal'
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <input
                id="movement-cost"
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="form-input"
              />
              {costPreview && <small className="help-text">{costPreview}</small>}
            </div>
          )}

          {isStockIn && (
            <div className="form-group">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={linkPurchaseOrder}
                  onChange={(e) => setLinkPurchaseOrder(e.target.checked)}
                />
                {t('inventory.movementsPage.linkPurchaseOrder')}
              </label>
              {linkPurchaseOrder && (
                <select
                  id="movement-purchase-order"
                  name="purchaseOrderId"
                  value={formData.purchaseOrderId}
                  onChange={handleChange}
                  className="form-input"
                  aria-label={t('inventory.movementsPage.purchaseOrder')}
                  disabled={purchaseOrders === null}
                >
                  <option value="">
                    {purchaseOrders === null
                      ? t('inventory.movementsPage.loadingPurchaseOrders')
                      : purchaseOrders.length === 0
                        ? t('inventory.movementsPage.noPurchaseOrders')
                        : t('inventory.movementsPage.selectPurchaseOrder')}
                  </option>
                  {(purchaseOrders || []).map((po) => (
                    <option key={po.orderId} value={po.orderId}>
                      {poLabel(po)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {isAbsolute && selectedItem && formData.quantity !== '' && (
            <div className="adjustment-preview">
              <p>
                {t('inventory.movementsPage.difference')}:{' '}
                <strong>
                  {(parseFloat(formData.quantity) - (Number(selectedItem.stock) || 0)).toFixed(2)}{' '}
                  {selectedItem.unit}
                </strong>
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="movement-reason">{t('inventory.movementsPage.reasonLabel')}</label>
            <textarea
              id="movement-reason"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              placeholder={t('inventory.movementsPage.reasonPlaceholder')}
              rows={2}
              className="form-input"
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? (
                t('inventory.movementsPage.saving')
              ) : (
                <>
                  <Save size={18} />
                  {t('inventory.movementsPage.registerMovement')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateMovementModal;
